import { Component, Element, h, Host, Prop, Watch, Event, EventEmitter, forceUpdate, State } from '@stencil/core';
import { awaitTopbar, en, reduceMotion } from '@/index';
import type { PnLanguages } from '@/index';

import { close as closeIcon } from 'pn-design-assets/pn-assets/icons.js';
import { PnTranslations, translations } from './translation';

/**
 * Present content in a modal that overlays everything on the current page.
 * Use the prop `open` to toggle the visiblity.
 * Use the event `modalToggle` to determine when the modal is being closed/opened by the user.
 *
 * @slot - The default slot, will be placed in the middle content area.
 * @slot header - Place the title + lead text in the header.
 * @slot buttons - Place buttons inside the modal at the bottom.
 */
@Component({
  tag: 'pn-modal',
  styleUrl: 'pn-modal.scss',
})
export class PnModal {
  private mo: MutationObserver;

  private readonly standardAnimationDuration = 400;
  private animationDuration: number = this.standardAnimationDuration;

  modalTimeout: NodeJS.Timeout;

  private modalElement: HTMLDialogElement;

  @Element() hostElement: HTMLElement;

  @State() isClosing: boolean = false;
  @State() isOpening: boolean = false;

  /** Set a label for the modal. */
  @Prop() label?: string;
  /** Set a descriptive text for the modal. */
  @Prop() helpertext?: string;
  /** Set the language. */
  @Prop() language?: PnLanguages = null;

  /** Bind to this property if you want to control the visibility of the modal from your own data. @category Features */
  @Prop({ mutable: true, reflect: true }) open?: boolean = false;
  /** Prevent users from closing the modal by clicking on the backdrop or the `ESC` key. @category Features */
  @Prop() persistent?: boolean = false;
  /** Hide the close button. If you disable this, make sure you build your own cancel button. @category Features */
  @Prop() hideClose?: boolean = false;
  /**
   * Allow overflow if possible. When the modal opens, it will check scrollHeight > offsetHeight.
   * Do not use if you have a lot of conditional rendering inside the modal.
   */
  @Prop() allowOverflow?: boolean = false;

  /** Use the `sheet` visual. Aligns the modal to the right. @category Visual */
  @Prop() sheet?: boolean = false;
  /** Define your own max width of the modal. Default is `45em`. @category Visual */
  @Prop() maxWidth?: string = null;

  @Watch('open')
  handleOpen() {
    if (this.open) this.openModal();
    else {
      this.closeModal();
      /** In the next update, we can remove this one so you can finally stack modals. */
      this.close.emit(true);
    }

    this.handleOverflow();

    clearTimeout(this.modalTimeout);
    this.isClosing = !this.open && true;

    if (reduceMotion()) this.animationDuration = 0;
    else this.animationDuration = this.standardAnimationDuration;

    this.modalTimeout = setTimeout(() => {
      this.isClosing = false;
      this.modalVisiblity.emit({ visible: this.open });
    }, this.animationDuration);
  }

  @Watch('maxWidth')
  handleMaxWidth() {
    const width = this.maxWidth || '45em';
    this.modalElement.style.setProperty('--pn-modal-max-width', width);
  }

  /** This event is fired when the modal is toggled. */
  @Event() modalToggle: EventEmitter<{ open: boolean }>;
  /** This event is dispatched after the opening/closing animation has finished playing. */
  @Event() modalVisiblity: EventEmitter<{ visible: boolean }>;
  /**
   * Event fired when the modal is closed, either by clicking on the dark background or by clicking on the close button.
   * @deprecated Use the new `modalToggle` event instead.
   */
  @Event() close: EventEmitter<boolean>;

  connectedCallback() {
    this.mo = new MutationObserver(() => forceUpdate(this.hostElement));
    this.mo.observe(this.hostElement, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    if (this.mo) this.mo.disconnect();
  }

  async componentWillLoad() {
    if (this.language === null) await awaitTopbar(this.hostElement);
  }

  componentDidLoad() {
    this.handleMaxWidth();
    if (this.open) this.openModal();
  }

  private translate(prop: PnTranslations) {
    return translations?.[prop]?.[this.language || en] || prop;
  }

  private handleOverflow() {
    if (!this.allowOverflow) return;
    const overflow = this.modalElement.scrollHeight > innerHeight * 0.85 ? 'hidden auto' : 'unset';
    this.modalElement.style.setProperty('--pn-modal-overflow', overflow);
  }

  private isSameModal(target: HTMLElement) {
    return this.modalElement.isSameNode(target);
  }

  private handleKeyboard(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!this.persistent) this.setModalClose();
  }

  private clickModalBackground(event: MouseEvent) {
    const { clientY, clientX } = event;

    const notInsideAnyModal = (event.target as HTMLDialogElement).localName !== this.modalElement.localName;
    const notThisModal = !this.isSameModal(event.target as HTMLElement);

    if (notInsideAnyModal || notThisModal) return;

    const { top, left, height, width } = this.modalElement.getBoundingClientRect();
    const isInModal = top <= clientY && clientY <= top + height && left <= clientX && clientX <= left + width;

    if (!isInModal && !this.persistent) this.toggleOpen(false);
  }

  private setModalClose() {
    this.toggleOpen(false);
  }

  private closeModal() {
    this.modalElement.close();
  }

  private openModal() {
    this.modalElement.showModal();
  }

  private toggleOpen(state?: boolean) {
    this.open = state ?? !this.open;
  }

  render() {
    return (
      <Host>
        <dialog
          class="pn-modal"
          data-closing={this.isClosing}
          data-sheet={this.sheet}
          onClose={() => this.setModalClose()}
          onCancel={() => this.setModalClose()}
          onToggle={() => this.modalToggle.emit({ open: this.open })}
          onKeyDown={event => this.handleKeyboard(event)}
          onClick={event => this.clickModalBackground(event)}
          ref={el => (this.modalElement = el)}
        >
          {!this.hideClose && (
            <pn-button
              small
              class="pn-modal-close-button"
              icon={closeIcon}
              iconOnly={true}
              arialabel={this.translate('CLOSE_MODAL')}
              appearance="light"
              variant="borderless"
              type="button"
              onPnClick={() => this.setModalClose()}
            />
          )}

          <header class="pn-modal-header">
            {this.label && <h2 class="pn-modal-label">{this.label}</h2>}
            {this.helpertext && <p class="pn-modal-text">{this.helpertext}</p>}

            <slot name="header"></slot>
          </header>

          <section class="pn-modal-content">
            <slot></slot>
          </section>

          <nav class="pn-modal-buttons">
            <slot name="buttons"></slot>
          </nav>
        </dialog>
      </Host>
    );
  }
}
